'use client';

import { Fragment, PropsWithChildren, useContext } from 'react';
import { FieldError, get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { RichText } from '@lamesarv-sdk/components';
import { CommonFormContext } from '@/components/CommonForm';
import { CommonCheckbox } from '@/components/CommonForm/CommonCheckbox';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { CommonOptions } from '@/components/CommonForm/CommonOptions';

import { IComponentGenericFormField, IComponentGenericEventRegistrationForm } from '@/types/cms';

import { PropsWithClasses } from '@lamesarv-sdk/types';
import { IEventRegistrationFormProps } from '../types';

import { checkBoxSMS, checkBoxSubscribe, checkboxTerms, messageError, messageSuccess } from '../constants';

// --

export interface IBaseSubmitProps extends IComponentGenericEventRegistrationForm, PropsWithClasses {
  styleClasses?: string;
  extraClasses?: string;
}

export const BaseSubmit = (props: Partial<IBaseSubmitProps>) => {
  const { isDisabled } = useContext(CommonFormContext);

  return (
    <button
      type="submit"
      disabled={isDisabled}
      className={twMerge(props.styleClasses, props.classes_submit, props.extraClasses)}
      data-tag-group={props.data_tag ? `${props.data_tag}--form-inquiry` : 'form-form-inquiry'}
      data-tag={props.data_tag ? `${props.data_tag}--form-inquiry-submit` : 'form-inquiry-submit'}
    >
      {props.submit_button_text || 'Submit'}
    </button>
  );
};

// --

export interface IBaseFieldsStylesProps {
  container?: string;
  label?: string;
  labelWithValue?: string;
  input?: string;
  inputWithValue?: string;
}

export interface IBaseFieldProps extends IComponentGenericFormField, PropsWithChildren {
  optionsList?: { label: string; value: string }[];
  styleClasses?: IBaseFieldsStylesProps;
  inputClasses?: { [input: string]: IBaseFieldsStylesProps };
  wrapperClasses?: string;
  extraClasses?: string;
}

export const BaseField = (props: IBaseFieldProps) => {
  let FormField: JSX.Element;

  switch (props.type) {
    case 'first-name':
      FormField = (
        <CommonInput
          label="First Name"
          name="firstName"
          isRequired={props.required}
          classContainer={twMerge(
            props.styleClasses.container,
            props.inputClasses?.firstName?.container,
            props.classes,
          )}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.firstName?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.firstName?.labelWithValue,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.firstName?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.firstName?.inputWithValue,
          )}
        />
      );
      break;
    case 'last-name':
      FormField = (
        <CommonInput
          label="Last Name"
          name="lastName"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.lastName?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.lastName?.label, props.classes_label)}
          classLabelWithValue={twMerge(props.styleClasses.labelWithValue, props.inputClasses?.lastName?.labelWithValue)}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.lastName?.input)}
          classInputWithValue={twMerge(props.styleClasses.inputWithValue, props.inputClasses?.lastName?.inputWithValue)}
        />
      );
      break;
    case 'email':
      FormField = (
        <CommonInput
          label="Email"
          name="email"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.email?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.email?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.email?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.email?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.email?.inputWithValue,
            props.classes_input,
          )}
          type="email"
        />
      );
      break;
    case 'phone':
      FormField = (
        <CommonMaskInput
          mask="(999) 999-9999"
          label="Phone Number"
          name="phone"
          isRequired={props.required}
          cleanValueCallback={(value) => value.replace(/\D/g, '')}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.phone?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.phone?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.phone?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.phone?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.phone?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'comments':
      FormField = (
        <CommonTextarea
          label="Comments"
          name="comments"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.comments?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.comments?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.comments?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.comments?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.comments?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'attendance':
      FormField = (
        <CommonOptions
          name="attendance"
          label="Which days do you plan to attend?"
          isRequired={props.required}
          optionsList={props.optionsList ?? []}
          classContainer={twMerge(props?.styleClasses?.container, props?.classes)}
          classContainerLabel={props?.classes_container_label}
          classLabel={twMerge(props?.styleClasses?.label, props?.classes_label)}
          classInput={twMerge(
            'w-4 h-4 focus:outline-none focus:ring-0',
            props?.styleClasses?.input,
            props?.classes_input,
          )}
        />
      );
      break;
    case 'interests':
      FormField = (
        <CommonOptions
          name="interests"
          label="Are you interested in:"
          isRequired={props.required}
          optionsList={props.optionsList ?? []}
          classContainer={twMerge(props?.styleClasses?.container, props?.classes)}
          classContainerLabel={props?.classes_container_label}
          classLabel={twMerge(props?.styleClasses?.label, props?.classes_label)}
          classInput={twMerge(
            'w-4 h-4 focus:outline-none focus:ring-0',
            props?.styleClasses?.input,
            props?.classes_input,
          )}
        />
      );
      break;
    default:
      FormField = null;
      break;
  }

  return (
    <div className={twMerge(props.wrapperClasses, props.extraClasses, props.type === 'comments' && 'col-span-2')}>
      {FormField}
      {props.children}
    </div>
  );
};

export const BaseTermsField = (props: IEventRegistrationFormProps) => {
  return (
    <CommonCheckbox
      name="terms"
      isRequired
      classContainer={twMerge('col-span-2 text-left items-start', props.classes)}
      className="items-start"
      classInput="w-4 h-4 focus:outline-none focus:ring-0 text-current"
    >
      {!props.custom_terms_text && <div dangerouslySetInnerHTML={{ __html: checkboxTerms }}></div>}
      {props.custom_terms_text && <RichText content={props.custom_terms_text} />}
    </CommonCheckbox>
  );
};

export const BaseSubscribeField = (props: IEventRegistrationFormProps) => {
  return (
    <CommonCheckbox
      name="subscribe"
      classContainer={twMerge('col-span-2 text-left items-start', props.classes)}
      className="items-start"
      classInput="w-4 h-4 focus:outline-none focus:ring-0 text-current"
    >
      {!props.custom_subscribe_text && <div>{checkBoxSubscribe}</div>}
      {props.custom_subscribe_text && <RichText content={props.custom_subscribe_text} />}
    </CommonCheckbox>
  );
};

export const BaseSMSField = (props: IEventRegistrationFormProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const errorDependency = get(errors, 'phone') as FieldError | null;
  const fieldValue = (watch('phone') || '').replace(/\D/g, '').length === 10;

  const isValid = fieldValue && !errorDependency;

  if (!isValid) {
    return null;
  }

  return (
    <CommonCheckbox
      name="smsOptIn"
      classContainer={twMerge('col-span-2 text-left items-start', props.classes)}
      className="items-start"
      classInput="border-y border-x w-4 h-4 focus:outline-none focus:ring-0"
    >
      {!props.custom_sms_text && <div>{checkBoxSMS}</div>}
      {props.custom_sms_text && <RichText content={props.custom_sms_text} className="[&>p]:!mb-0" />}
    </CommonCheckbox>
  );
};

// --

interface IBaseMessagesProps extends IEventRegistrationFormProps {
  wasSuccessful: boolean;
  wasError: boolean;
}

export const BaseMessages = (props: IBaseMessagesProps) => {
  return (
    <Fragment>
      {props.wasSuccessful && (
        <div className="flex flex-row items-center gap-2 my-4">
          <FontAwesomeIcon className="w-5 h-5 text-green-500 -mt-1" icon={faCheck} />
          <span data-tag-group="form-success-label" className="text-black-500 font-medium">
            {props.message_success || messageSuccess}
          </span>
        </div>
      )}
      {props.wasError && (
        <div className="flex flex-row items-center gap-2 my-4">
          <FontAwesomeIcon className="w-5 h-5 text-red-700 -mt-1" icon={faXmark} />
          <span className="text-black-500 font-medium leading-none">{props.message_error || messageError}</span>
        </div>
      )}
    </Fragment>
  );
};
